import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";
import { ExternalLink } from "ui/components";
import { sepolia, arbitrumSepolia } from "viem/chains";
import { z } from "zod";

const LZ_PENDING_BRIDGE_KEY = "lz_pending_bridge";
const POLL_INTERVAL = 10_000;
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes
const TOAST_ID = "pending-bridge";

const TESTNET_CHAIN_IDS: Set<number> = new Set([sepolia.id, arbitrumSepolia.id]);
const BRIDGE_SAVED_EVENT = "lz_bridge_saved";
const QUERY_KEY = ["lz-pending-bridge"];

const pendingBridgeSchema = z.object({
  txHash: z.string(),
  srcChainId: z.number(),
  timestamp: z.number(),
});

type PendingBridge = z.infer<typeof pendingBridgeSchema>;

enum BridgeStatus {
  INFLIGHT = "INFLIGHT",
  CONFIRMING = "CONFIRMING",
  DELIVERED = "DELIVERED",
  FAILED = "FAILED",
  BLOCKED = "BLOCKED",
  TIMEOUT = "TIMEOUT",
}

export function savePendingBridge(entry: PendingBridge) {
  localStorage.setItem(LZ_PENDING_BRIDGE_KEY, JSON.stringify(entry));
  window.dispatchEvent(new Event(BRIDGE_SAVED_EVENT));
}

function loadPendingBridge(): PendingBridge | null {
  const raw = localStorage.getItem(LZ_PENDING_BRIDGE_KEY);
  if (!raw) return null;
  const result = pendingBridgeSchema.safeParse(JSON.parse(raw));
  return result.success ? result.data : null;
}

function clearPendingBridge() {
  localStorage.removeItem(LZ_PENDING_BRIDGE_KEY);
}

function getLzScanBaseUrl(srcChainId: number): string {
  return TESTNET_CHAIN_IDS.has(srcChainId)
    ? "https://scan-testnet.layerzero-api.com/v1"
    : "https://scan.layerzero-api.com/v1";
}

function getLzScanTxUrl(txHash: string, srcChainId: number): string {
  const base = TESTNET_CHAIN_IDS.has(srcChainId) ? "https://testnet.layerzeroscan.com" : "https://layerzeroscan.com";
  return `${base}/tx/${txHash}`;
}

async function fetchBridgeStatus(pending: PendingBridge): Promise<BridgeStatus> {
  const baseUrl = getLzScanBaseUrl(pending.srcChainId);
  const res = await fetch(`${baseUrl}/messages/tx/${pending.txHash}`);
  if (!res.ok) return BridgeStatus.INFLIGHT;
  const data = await res.json();
  return (data.data?.[0]?.status?.name as BridgeStatus) ?? BridgeStatus.INFLIGHT;
}

export function usePendingBridgeToast() {
  const queryClient = useQueryClient();

  // Re-read localStorage when a bridge is saved in the same session
  useEffect(() => {
    const onBridgeSaved = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    window.addEventListener(BRIDGE_SAVED_EVENT, onBridgeSaved);
    return () => window.removeEventListener(BRIDGE_SAVED_EVENT, onBridgeSaved);
  }, [queryClient]);

  const { data: status } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const entry = loadPendingBridge();
      if (!entry) return null;

      if (Date.now() - entry.timestamp > MAX_AGE_MS) {
        clearPendingBridge();
        return BridgeStatus.TIMEOUT;
      }
      return await fetchBridgeStatus(entry);
    },
    refetchInterval: (query) => {
      const s = query.state.data;
      if (
        !s ||
        s === BridgeStatus.DELIVERED ||
        s === BridgeStatus.FAILED ||
        s === BridgeStatus.BLOCKED ||
        s === BridgeStatus.TIMEOUT
      )
        return false;
      return POLL_INTERVAL;
    },
  });

  // Manage toast based on query status
  useEffect(() => {
    const entry = loadPendingBridge();
    if (!entry || !status) return;

    if (status === BridgeStatus.DELIVERED) {
      clearPendingBridge();
      toast.success("Bridge transfer complete!", { id: TOAST_ID, duration: Infinity });
    } else if (status === BridgeStatus.FAILED || status === BridgeStatus.BLOCKED) {
      clearPendingBridge();
      toast.error(`Bridge transfer ${status.toLowerCase()}`, { id: TOAST_ID, duration: Infinity });
    } else if (status === BridgeStatus.TIMEOUT) {
      toast.dismiss(TOAST_ID);
    } else {
      const scanUrl = getLzScanTxUrl(entry.txHash, entry.srcChainId);
      toast.loading(`Transfer is in progress...`, {
        id: TOAST_ID,
        duration: Infinity,
        description: (
          <ExternalLink href={scanUrl} className="underline">
            View
          </ExternalLink>
        ),
      });
    }
  }, [status]);

  const isPending = Boolean(
    status && status !== "DELIVERED" && status !== "FAILED" && status !== "BLOCKED" && status !== "TIMEOUT",
  );

  return { isPending };
}
